import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ModalDialog,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogNav,
  useScreenClass,
} from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import addressPropType from './AddressConstants';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { requestShippingAddressChange } from '../../../apis/orderUpdateRequestsApi';
import { AddressUpdateConfirmation } from './AddressUpdateConfirmation';
import { AddressUpdateTerms } from './AddressUpdateTerms';
import { AddressUpdateForm } from './AddressUpdateForm';

/**
 * This component provides a modal that contains the address form that allows a
 * customer to edit the shipping address of their order.
 * @param address the current address of the order
 * @param isModalInitiallyOpen
 * @param closeModal function to call when closing the modal
 * @param headerText display text of the modals header
 * @returns {JSX.Element}
 * @constructor
 */
export const UpdateShippingAddressModal = ({
  address,
  isModalInitiallyOpen,
  closeModal,
}) => {
  const [sharedActionMessages, shippingAddressUpdate] = useTranslations([
    'sharedActionMessages',
    'shippingAddressUpdate',
  ]);
  const [isModalOpen, setIsModalOpen] = useState(isModalInitiallyOpen);

  const { orderNumber, vistacartTenantName, tenantEnvironment } =
    useContext(OrderDetailsContext);

  const [isTermsAccepted, setTermsAccepted] = useState(false);
  const [updateRequestFailure, setUpdateRequestFailure] = useState(null);
  const [updateRequestSuccess, setUpdateRequestSuccess] = useState(null);
  const [updateRequestMade, setUpdateRequestMade] = useState(false);

  const resetUpdateRequest = () => {
    closeModal();
    setIsModalOpen(false);
    setTermsAccepted(false);
    setUpdateRequestMade(false);
    setUpdateRequestFailure(null);
    setUpdateRequestSuccess(null);
  };

  const saveAddress = async addressToUpdate => {
    setUpdateRequestMade(true);
    try {
      await requestShippingAddressChange({
        tenantEnvironment,
        orderNumber,
        addressToUpdate,
        vistacartTenantName,
      });
      // if it makes it here, thats a success! the success modal will be presented
      setUpdateRequestSuccess(true);
    } catch (err) {
      if (err && err.failureType === 'INVALID') {
        const errorObj = {
          failureReason: 'CHANGE_NOT_ALLOWED',
          details: shippingAddressUpdate.couldNotBeUpdatedSpecificDetails,
        };
        setUpdateRequestFailure(errorObj);
      } else {
        const errorObj = {
          failureReason: 'UNKNOWN',
          details: shippingAddressUpdate.couldNotBeUpdatedGeneralDetails,
        };
        setUpdateRequestFailure(errorObj);
      }
    }
  };

  const screenClass = useScreenClass();
  const modalWidthStyle = screenClass === 'xs' ? {} : { width: '600px' };
  // Needed as SWAN puts a higher zIndex on the modal, covering the autocomplete combobox
  const zIndexOverride = { zIndex: 9000 };

  return (
    <ModalDialog
      isOpen={isModalOpen}
      onRequestDismiss={resetUpdateRequest}
      variant="standard"
      style={{ ...zIndexOverride }}
      onlyRenderWhenOpen
    >
      <ModalDialogContent style={modalWidthStyle}>
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={sharedActionMessages.close} />
        </ModalDialogNav>

        {!updateRequestMade && !isTermsAccepted ? (
          <AddressUpdateTerms setTermsAccepted={setTermsAccepted} />
        ) : null}

        {!updateRequestMade && isTermsAccepted ? (
          <AddressUpdateForm
            address={address}
            closeModal={closeModal}
            saveAddress={saveAddress}
          />
        ) : null}

        {updateRequestMade ? (
          <AddressUpdateConfirmation
            updateRequestFailure={updateRequestFailure}
            updateRequestSuccess={updateRequestSuccess}
            onRequestDismiss={resetUpdateRequest}
          />
        ) : null}
      </ModalDialogContent>
    </ModalDialog>
  );
};

UpdateShippingAddressModal.propTypes = {
  address: PropTypes.exact(addressPropType),
  closeModal: PropTypes.func.isRequired,
  isModalInitiallyOpen: PropTypes.bool,
};
